import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { authApi } from '@/api';
import { ApiError } from '@/api/client';
import {
  BusyButton,
  CardIntro,
  EmailChip,
  FieldError,
  FormAlert,
  PasswordInput,
  PasswordRules,
  StatusMark,
  StepProgress,
  passwordMsg,
} from '@/components/auth-kit';

const schema = z
  .object({
    password: z.string().superRefine((v, ctx) => {
      const m = passwordMsg(v);
      if (m) ctx.addIssue({ code: 'custom', message: m });
    }),
    confirmPassword: z.string().min(1, 'Repeat the new password.'),
  })
  .refine((d) => !d.confirmPassword || d.password === d.confirmPassword, {
    message: 'Passwords don’t match.',
    path: ['confirmPassword'],
  });
type FormData = z.infer<typeof schema>;

type Phase = 'validating' | 'form' | 'expired' | 'done';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [phase, setPhase] = useState<Phase>('validating');
  const [email, setEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
    getValues,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { password: '', confirmPassword: '' },
  });
  const password = watch('password');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setPhase('expired');
        return;
      }
      try {
        const result = await authApi.validateResetToken(token);
        if (cancelled) return;
        if (result.email) setEmail(result.email);
        setPhase(result.valid ? 'form' : 'expired');
      } catch {
        if (!cancelled) setPhase('expired');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const titles: Record<Phase, string> = {
      validating: 'Reset password',
      form: 'Choose a new password',
      expired: 'Link expired',
      done: 'Password reset',
    };
    document.title = `${titles[phase]} · authrz.me`;
  }, [phase]);

  const onSubmit = async (data: FormData) => {
    if (!token) return;
    setAlert(null);
    setIsLoading(true);
    try {
      await authApi.resetPassword({ token, password: data.password });
      reset();
      setPhase('done');
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && error.code === 'invalid_token') {
        setPhase('expired');
      } else {
        setAlert(
          error instanceof Error ? error.message : 'Failed to reset password. Try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (phase === 'validating') {
    return (
      <section
        className="flex flex-col items-center gap-3 py-12 text-center"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <p className="text-[13px] text-muted-foreground">Checking your reset link…</p>
      </section>
    );
  }

  if (phase === 'expired') {
    return (
      <section className="flex flex-col gap-6" aria-labelledby="t5">
        <CardIntro
          id="t5"
          mark={
            <StatusMark tone="bad">
              <XCircle />
            </StatusMark>
          }
          title="This reset link has expired"
          sub="Reset links work once and expire after a short time. Request a new one to continue."
        />
        <div className="flex flex-col gap-2">
          <Button asChild size="lg" className="w-full">
            <Link to="/forgot-password">Request a new link</Link>
          </Button>
          <Button asChild variant="ghost" className="w-full">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      </section>
    );
  }

  if (phase === 'done') {
    return (
      <section className="flex flex-col gap-6" aria-labelledby="t4" aria-live="polite">
        <CardIntro
          id="t4"
          mark={
            <StatusMark tone="ok">
              <CheckCircle2 />
            </StatusMark>
          }
          title="Password reset"
          sub={
            <>
              Your password{email && <> for <EmailChip>{email}</EmailChip></>} has changed. Every
              other session was signed out.
            </>
          }
        />
        <Button asChild size="lg" className="w-full">
          <Link to="/login">Sign in with the new password</Link>
        </Button>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6" aria-labelledby="t3">
      <StepProgress step={3} />
      <CardIntro
        id="t3"
        title="Choose a new password"
        sub={
          email ? (
            <>
              For <EmailChip>{email}</EmailChip>. Use one you don’t use anywhere else.
            </>
          ) : (
            'Use one you don’t use anywhere else.'
          )
        }
      />
      {alert && <FormAlert title="Couldn’t reset your password">{alert}</FormAlert>}
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="password">New password</Label>
          <PasswordInput
            id="password"
            placeholder="Enter a new password"
            autoComplete="new-password"
            autoFocus
            aria-describedby="pwRules"
            aria-invalid={!!errors.password}
            {...register('password', {
              onChange: () => {
                if (getValues('confirmPassword')) void trigger('confirmPassword');
              },
            })}
          />
          <PasswordRules id="pwRules" value={password} />
          <FieldError id="password-err">{errors.password?.message}</FieldError>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirmPassword">Confirm new password</Label>
          <PasswordInput
            id="confirmPassword"
            placeholder="Repeat the new password"
            autoComplete="new-password"
            aria-invalid={!!errors.confirmPassword}
            aria-describedby={errors.confirmPassword ? 'confirm-err' : undefined}
            {...register('confirmPassword')}
          />
          <FieldError id="confirm-err">{errors.confirmPassword?.message}</FieldError>
        </div>
        <BusyButton type="submit" busy={isLoading} busyLabel="Saving…">
          Reset password
        </BusyButton>
      </form>
    </section>
  );
}
